/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { Menu, X, ArrowRight, LogOut, Wallet, Brain } from 'lucide-react';
import { WalletProvider, useWallet } from './providers/WalletProvider';
import { AiProvider } from './providers/ai-provider';
import { TradingProvider } from './context/TradingContext';
import { SwapProvider } from './context/SwapContext';
import { NotificationProvider } from './context/NotificationContext';
import { WatchlistProvider } from './context/WatchlistContext';

import { Launchpad } from './components/Launchpad';
import { AllMarkets } from './components/AllMarkets';
import { AiDesk } from './pages/AiDesk';
import { AiAssistantPage } from './pages/AiAssistantPage';
import { About } from './pages/About';
import { Fees } from './pages/Fees';
import { Legal } from './pages/Legal';
import { ApiAccess } from './pages/ApiAccess';
import { HelpCenter } from './pages/HelpCenter';
import { RankingPage } from './pages/Ranking';

import { WalletModal } from './components/WalletModal';
import { NotificationCenter } from './components/NotificationCenter';
import { Footer } from './components/Footer';
import { LogoIcon } from './components/logo-icon';
import { NetworkSelector } from './components/NetworkSelector';

import { QuantumScene } from './components/QuantumScene';
import { Features } from './components/Features';
import { RunningTokens } from './components/RunningTokens';
import { TransactionSummary } from './components/TransactionSummary';
import { FAQ } from './components/FAQ';
import { AnalysisPopupCard } from './components/analysis-popup-card';
import { TradeModal } from './components/TradeModal';
import { AiSignal } from './types/ai';
import { SmartMoneyPopup } from './components/SmartMoneyPopup';
import { AiSignalSubscriber } from './components/AiSignalSubscriber';

const HomeView = ({ setCurrentView, onOpenSmartMoney }: { setCurrentView: (view: string) => void; onOpenSmartMoney: () => void; }) => {
    return (
        <div className="relative w-full overflow-x-hidden">
            {/* Hero Section */}
            <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
                <QuantumScene />
                <div className="container mx-auto px-6 relative z-10 pt-20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="text-left space-y-8">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/80 backdrop-blur-md border border-stone-200 rounded-full shadow-soft-md animate-fade-in">
                                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                <span className="text-xs font-bold text-stone-600 uppercase tracking-widest">Mainnet Beta Live</span>
                            </div>
                            
                            <h1 className="font-serif text-5xl md:text-7xl text-stone-900 leading-[1.1]">
                                The Foundry for <br/>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-nobel-gold to-amber-600">Digital Assets</span>
                            </h1>
                            
                            <p className="text-xl text-stone-500 max-w-lg leading-relaxed">
                                Launch, trade, and scale on Solana & Sui with one unified interface. 
                                Powered by AI-driven market intelligence.
                            </p>

                            <div className="flex flex-col sm:flex-row gap-4">
                                <button 
                                    onClick={() => setCurrentView('LAUNCH')}
                                    className="px-8 py-4 bg-stone-900 text-white rounded-xl font-bold text-sm hover:bg-nobel-gold hover:text-stone-900 transition-all shadow-soft-xl hover:shadow-soft-2xl btn-shine"
                                >
                                    <span className="btn-shine-content flex items-center justify-center gap-2">
                                        Start Launching
                                    </span>
                                </button>
                                <button 
                                    onClick={() => setCurrentView('ALL_MARKETS')}
                                    className="px-8 py-4 bg-white text-stone-900 border border-stone-200 rounded-xl font-bold text-sm hover:bg-stone-50 transition-colors shadow-soft-lg flex items-center justify-center effect-frame"
                                >
                                    Explore Markets
                                </button>
                            </div>
                        </div>

                        {/* Hero Stats/Visuals */}
                        <div className="hidden lg:block h-[500px]">
                             <TransactionSummary />
                        </div>
                    </div>
                </div>
            </section>

            <Features />

            {/* AI Trading Bot Promo Section */}
            <section className="py-24 bg-[#1a1a1a] text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-nobel-gold/5 rounded-full blur-[120px] pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-900/10 rounded-full blur-[100px] pointer-events-none"></div>

                <div className="container mx-auto px-6 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div className="space-y-8">
                            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full text-nobel-gold text-xs font-bold uppercase tracking-widest backdrop-blur-md">
                                <Brain size={14} /> Autonomous Agent
                            </div>
                            <h2 className="font-serif text-4xl md:text-6xl leading-[1.1]">
                                Your Personal <br/>
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-nobel-gold to-amber-200">Hedge Fund</span>
                            </h2>
                            <p className="text-lg text-stone-400 leading-relaxed max-w-xl">
                                Stop staring at charts. Our Swarm Neural Engine scans 500+ signals per second to detect whale accumulation and liquidity spikes. Configure your risk appetite and let the bot execute.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <button 
                                    onClick={() => setCurrentView('AI_DESK')}
                                    className="px-8 py-4 bg-nobel-gold text-stone-900 rounded-xl font-bold text-sm hover:bg-white transition-all shadow-[0_0_30px_rgba(197,160,89,0.2)] flex items-center justify-center gap-2 group"
                                >
                                    Configure Bot <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        </div>

                        <div className="relative">
                            {/* Abstract Bot UI Visualization */}
                            <div className="bg-stone-900/80 backdrop-blur-xl border border-stone-800 p-8 rounded-3xl shadow-2xl relative overflow-hidden">
                                <div className="flex justify-between items-center mb-8 border-b border-stone-800 pb-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_#22c55e]"></div>
                                        <span className="font-mono font-bold text-stone-300 tracking-widest">SYSTEM ONLINE</span>
                                    </div>
                                    <span className="text-xs font-mono text-stone-500">v2.4.1</span>
                                </div>
                                <div className="space-y-4 font-mono text-sm">
                                    <div className="flex justify-between text-stone-400">
                                        <span>Active Nodes</span>
                                        <span className="text-white">128</span>
                                    </div>
                                    <div className="flex justify-between text-stone-400">
                                        <span>Signal Confidence</span>
                                        <span className="text-green-400">92.4%</span>
                                    </div>
                                    <div className="flex justify-between text-stone-400">
                                        <span>24h PnL</span>
                                        <span className="text-nobel-gold">+14.2%</span>
                                    </div>
                                    <div className="h-32 mt-6 relative bg-stone-800/50 rounded-xl overflow-hidden flex items-end justify-between px-2 pb-2 gap-1">
                                        {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95].map((h, i) => (
                                            <div key={i} className="w-full bg-nobel-gold/20 rounded-sm" style={{ height: `${h}%` }}>
                                                <div className="w-full bg-nobel-gold absolute bottom-0 left-0 right-0 rounded-sm" style={{ height: `${h}%`, opacity: 0.5 }}></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trending Section */}
            <section id="market" className="py-20 bg-white relative z-20">
                <div className="container mx-auto px-6">
                    <div className="flex justify-between items-end mb-12">
                        <div>
                            <h2 className="font-serif text-4xl text-stone-900 mb-2">Live Markets</h2>
                            <p className="text-stone-500">Top performing assets on the bonding curve.</p>
                        </div>
                        <button onClick={() => setCurrentView('ALL_MARKETS')} className="text-nobel-gold font-bold hover:text-stone-900 transition-colors flex items-center gap-1">
                            View Terminal <ArrowRight size={16} />
                        </button>
                    </div>
                    <RunningTokens />
                </div>
            </section>
            
            <FAQ />
        </div>
    );
};

const AppContent = () => {
    const [currentView, setCurrentView] = useState('HOME');
    const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
    const { isConnected, address, disconnect } = useWallet();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    // State for modals
    const [signalToAnalyze, setSignalToAnalyze] = useState<AiSignal | null>(null);
    const [tradeModalFromSignal, setTradeModalFromSignal] = useState<AiSignal | null>(null);
    const [isSmartMoneyPopupOpen, setIsSmartMoneyPopupOpen] = useState(false);

    // Listen for custom event to open analysis modal
    useEffect(() => {
        const handleOpenSignal = (event: CustomEvent<AiSignal>) => {
            setSignalToAnalyze(event.detail);
        };
        
        window.addEventListener('open-ai-signal', handleOpenSignal as EventListener);
        return () => window.removeEventListener('open-ai-signal', handleOpenSignal as EventListener);
    }, []);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const renderView = () => {
        switch(currentView) {
            case 'HOME': return <HomeView setCurrentView={setCurrentView} onOpenSmartMoney={() => setIsSmartMoneyPopupOpen(true)} />;
            case 'LAUNCH': return <Launchpad onBack={() => setCurrentView('HOME')} onGoToMarket={() => setCurrentView('ALL_MARKETS')} onConnectWallet={() => setIsWalletModalOpen(true)} />;
            case 'ALL_MARKETS': return <AllMarkets onBack={() => setCurrentView('HOME')} />;
            case 'RANKING': return <RankingPage />;
            case 'AI_DESK': return <AiDesk onBack={() => setCurrentView('HOME')} onLaunchAssistant={() => setCurrentView('AI_CHAT')} />;
            case 'AI_CHAT': return <AiAssistantPage onBack={() => setCurrentView('AI_DESK')} />;
            case 'ABOUT': return <About onBack={() => setCurrentView('HOME')} />;
            case 'FEES': return <Fees />;
            case 'LEGAL': return <Legal />;
            case 'HELP': return <HelpCenter onBack={() => setCurrentView('HOME')} />;
            case 'API_ACCESS': return <ApiAccess onBack={() => setCurrentView('HOME')} onConnectWallet={() => setIsWalletModalOpen(true)} />;
            default: return <HomeView setCurrentView={setCurrentView} onOpenSmartMoney={() => setIsSmartMoneyPopupOpen(true)} />;
        }
    };

    return (
        <div className="min-h-screen bg-[#F9F8F4] text-stone-900 font-sans selection:bg-nobel-gold selection:text-white w-full overflow-x-hidden">
            <WalletModal isOpen={isWalletModalOpen} onClose={() => setIsWalletModalOpen(false)} />
            <NotificationCenter />
            <SmartMoneyPopup isOpen={isSmartMoneyPopupOpen} onClose={() => setIsSmartMoneyPopupOpen(false)} />
            
            {/* Modals for AI Signal interaction */}
            {signalToAnalyze && (
                <AnalysisPopupCard 
                    signal={signalToAnalyze} 
                    onClose={() => setSignalToAnalyze(null)} 
                    onExecute={() => {
                        setTradeModalFromSignal(signalToAnalyze);
                        setSignalToAnalyze(null);
                    }} 
                />
            )}
            {tradeModalFromSignal && (
                 <TradeModal 
                    token={tradeModalFromSignal.token}
                    currentPrice={tradeModalFromSignal.priceAtSignal}
                    isOpen={!!tradeModalFromSignal}
                    onClose={() => setTradeModalFromSignal(null)}
                    initialSide={tradeModalFromSignal.type}
                 />
            )}
            
            {/* Global Navbar - Hide on Chat Page for immersion */}
            {currentView !== 'AI_CHAT' && (
                <nav className={`fixed top-0 w-full z-50 transition-all duration-300 bg-white/80 backdrop-blur-md border-b border-stone-200 py-3 ${!isScrolled ? 'lg:bg-transparent lg:border-transparent lg:py-5 lg:shadow-none' : 'shadow-soft-lg'}`}>
                    <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
                        {/* Logo Area */}
                        <div className="flex items-center gap-3 cursor-pointer z-50" onClick={() => { setCurrentView('HOME'); setIsMobileMenuOpen(false); }}>
                            <div className="w-8 h-8 rounded-full bg-stone-900 border-2 border-stone-200 flex items-center justify-center shadow-md overflow-hidden">
                                <img src="/assets/logo.svg" alt="The Foundry" className="w-full h-full object-cover p-1.5" />
                            </div>
                            <span className="font-serif font-bold text-xl tracking-tight"><span className="font-light">THE</span> FOUNDRY</span>
                        </div>

                        {/* Desktop Nav (Changed to lg:flex for tablet support) */}
                        <div className="hidden lg:flex items-center gap-2 text-sm font-bold text-stone-600">
                            <button onClick={() => setCurrentView('HOME')} className="px-3 py-2 rounded-md hover:text-nobel-gold hover:bg-stone-100/50 transition-colors">Home</button>
                            <button onClick={() => setCurrentView('ALL_MARKETS')} className="px-3 py-2 rounded-md hover:text-nobel-gold hover:bg-stone-100/50 transition-colors">Markets</button>
                            <button onClick={() => setCurrentView('RANKING')} className="px-3 py-2 rounded-md hover:text-nobel-gold hover:bg-stone-100/50 transition-colors">Ranking</button>
                            <button onClick={() => setCurrentView('LAUNCH')} className="px-3 py-2 rounded-md hover:text-nobel-gold hover:bg-stone-100/50 transition-colors">Launchpad</button>
                            <button onClick={() => setCurrentView('AI_DESK')} className="px-3 py-2 rounded-md hover:text-nobel-gold hover:bg-stone-100/50 transition-colors">AI Desk</button>
                            <button onClick={() => setCurrentView('ABOUT')} className="px-3 py-2 rounded-md hover:text-nobel-gold hover:bg-stone-100/50 transition-colors">About</button>
                        </div>
                        
                        {/* Desktop Actions (Changed to lg:flex for tablet support) */}
                        <div className="hidden lg:flex items-center gap-3">
                            <NetworkSelector />
                            
                            {isConnected ? (
                                <div className="flex items-center gap-2">
                                    <div className="px-3 py-2 bg-stone-100 text-stone-900 rounded-md font-bold text-xs border border-stone-200 flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                        <span>{address?.slice(0, 4)}...{address?.slice(-4)}</span>
                                    </div>
                                    <button 
                                        onClick={disconnect}
                                        className="p-2 bg-stone-100 hover:bg-red-50 text-stone-500 hover:text-red-500 rounded-md transition-colors border border-stone-200"
                                        title="Disconnect Wallet"
                                    >
                                        <LogOut size={16} />
                                    </button>
                                </div>
                            ) : (
                                <button 
                                    onClick={() => setIsWalletModalOpen(true)}
                                    className="px-4 md:px-6 py-2.5 bg-stone-900 text-white rounded-md font-bold text-xs hover:bg-nobel-gold hover:text-stone-900 transition-colors shadow-soft-lg whitespace-nowrap btn-shine flex items-center gap-2"
                                >
                                    <Wallet size={16} />
                                    <span className="btn-shine-content">
                                        Connect Wallet
                                    </span>
                                </button>
                            )}
                        </div>

                        {/* Mobile/Tablet Menu Toggle (Changed to lg:hidden) */}
                        <div className="flex items-center lg:hidden">
                            <button 
                                className="p-2 text-stone-600 hover:bg-stone-100 rounded-md transition-colors"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                aria-label="Toggle menu"
                            >
                                <Menu size={24} />
                            </button>
                        </div>
                    </div>
                </nav>
            )}

            {/* Mobile Sidebar */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 animate-fade-in lg:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                    aria-hidden="true"
                ></div>
            )}

            <div
                className={`fixed top-0 left-0 h-full w-72 bg-white z-50 shadow-lg transform transition-transform duration-300 ease-in-out lg:hidden flex flex-col
                    ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
                role="dialog"
                aria-modal="true"
            >
                <div className="p-6 border-b border-stone-200 flex justify-between items-center">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => { setCurrentView('HOME'); setIsMobileMenuOpen(false); }}>
                        <div className="w-8 h-8 rounded-full bg-stone-900 border-2 border-stone-200 flex items-center justify-center shadow-md overflow-hidden">
                            <img src="/assets/logo.svg" alt="The Foundry" className="w-full h-full object-cover p-1.5" />
                        </div>
                        <span className="font-serif font-bold text-xl tracking-tight"><span className="font-light">THE</span> FOUNDRY</span>
                    </div>
                    <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 rounded-full hover:bg-stone-100" aria-label="Close menu"><X size={24} /></button>
                </div>
                <div className="flex flex-col items-start p-6 gap-6 text-lg font-serif font-bold text-stone-900">
                    <button onClick={() => { setCurrentView('HOME'); setIsMobileMenuOpen(false); }} className="hover:text-nobel-gold transition-colors">Home</button>
                    <button onClick={() => { setCurrentView('ALL_MARKETS'); setIsMobileMenuOpen(false); }} className="hover:text-nobel-gold transition-colors">Markets</button>
                    <button onClick={() => { setCurrentView('RANKING'); setIsMobileMenuOpen(false); }} className="hover:text-nobel-gold transition-colors">Ranking</button>
                    <button onClick={() => { setCurrentView('AI_DESK'); setIsMobileMenuOpen(false); }} className="hover:text-nobel-gold transition-colors">AI Desk</button>
                    <button onClick={() => { setCurrentView('LAUNCH'); setIsMobileMenuOpen(false); }} className="hover:text-nobel-gold transition-colors">Launchpad</button>
                    <button onClick={() => { setCurrentView('ABOUT'); setIsMobileMenuOpen(false); }} className="hover:text-nobel-gold transition-colors">About</button>
                </div>
                <div className="mt-auto p-6 flex flex-col items-center gap-4 border-t border-stone-200">
                    <NetworkSelector />
                    {isConnected ? (
                        <button
                            onClick={() => { disconnect(); setIsMobileMenuOpen(false); }}
                            className="w-full px-6 py-3 bg-red-50 text-red-600 rounded-lg font-bold text-sm border border-red-100 flex items-center justify-center gap-2"
                        >
                            <LogOut size={16} /> Disconnect
                        </button>
                    ) : (
                        <button
                            onClick={() => { setIsWalletModalOpen(true); setIsMobileMenuOpen(false); }}
                            className="w-full px-8 py-3 bg-stone-900 text-white rounded-lg font-bold text-sm flex items-center justify-center gap-2"
                        >
                            <Wallet size={16} /> Connect Wallet
                        </button>
                    )}
                </div>
            </div>

            <div className={currentView === 'HOME' || currentView === 'AI_CHAT' ? '' : 'pt-20'}>
                {renderView()}
            </div>

            {currentView !== 'AI_CHAT' && <Footer onNavigate={(view) => { setCurrentView(view); window.scrollTo(0,0); }} />}
        </div>
    )
};

const App = () => {
    return (
        <WalletProvider>
            <NotificationProvider>
                <AiProvider>
                    <WatchlistProvider>
                        <TradingProvider>
                            <SwapProvider>
                                <AiSignalSubscriber />
                                <AppContent />
                            </SwapProvider>
                        </TradingProvider>
                    </WatchlistProvider>
                </AiProvider>
            </NotificationProvider>
        </WalletProvider>
    );
};

export default App;